import type { Worker } from '@/types'

/** Wildcard category: an active skill without a category (the worker can do any category). */
export const ANY_CATEGORY = '*'

export interface WorkerCategoryInfo {
  /** Category ids (plus ANY_CATEGORY) the worker can do. */
  categories: string[]
  /** Category names from the worker's active skills (for the name fallback and display). */
  categoryNames: string[]
}

/** Keyed by the same id the schedule overview uses for workers (worker_id, else id). */
export type WorkerCategoryIndex = Map<string, WorkerCategoryInfo>

const normalizeName = (value: string | null | undefined) => String(value || '').trim().toLowerCase()

/**
 * Categories a worker can do: their category list plus the categories of their active skills;
 * a skill without a category applies to every category. Same rule as the Real Time page / backend.
 */
export function resolveWorkerCategories(worker: Pick<Worker, 'categories' | 'worker_skill_mappings'>): string[] {
  const categories = new Set((worker.categories ?? []).map((id) => String(id)))
  ;(worker.worker_skill_mappings ?? []).forEach((mapping) => {
    if (mapping.is_active === false) return
    categories.add(mapping.job_category_id == null || mapping.job_category_id === '' ? ANY_CATEGORY : String(mapping.job_category_id))
  })
  return Array.from(categories)
}

export function buildWorkerCategoryIndex(workers: Worker[]): WorkerCategoryIndex {
  const index: WorkerCategoryIndex = new Map()
  workers.forEach((worker) => {
    const names = (worker.worker_skill_mappings ?? [])
      .filter((mapping) => mapping.is_active !== false && String(mapping.job_category_name || '').trim())
      .map((mapping) => String(mapping.job_category_name).trim())
    index.set(String(worker.worker_id ?? worker.id), {
      categories: resolveWorkerCategories(worker),
      categoryNames: Array.from(new Set(names)),
    })
  })
  return index
}

/**
 * Same as the Real Time page: a job without a category accepts any worker; otherwise the worker
 * needs that category or an any-category skill. No category info = no match.
 */
export function workerMatchesCategory(info: WorkerCategoryInfo | undefined, categoryId: string | null | undefined): boolean {
  if (!categoryId) return true
  const categories = info?.categories ?? []
  return categories.includes(ANY_CATEGORY) || categories.includes(categoryId)
}

/** Fallback while the job's category id is unknown: match the job's category name to skill names. */
export function workerMatchesCategoryName(info: WorkerCategoryInfo | undefined, categoryName: string | null | undefined): boolean {
  const needle = normalizeName(categoryName)
  if (!needle) return true
  if (!info) return false
  return info.categories.includes(ANY_CATEGORY) || info.categoryNames.some((name) => normalizeName(name) === needle)
}

/** "Cleaning, Security" / "Any category" / null when unknown. */
export function describeWorkerCategories(info: WorkerCategoryInfo | undefined): string | null {
  if (!info) return null
  if (info.categoryNames.length > 0) return info.categoryNames.join(', ')
  return info.categories.includes(ANY_CATEGORY) ? 'Any category' : null
}

/**
 * Splits workers into the job's category and the rest. `categoryId` (from the job detail) wins;
 * without it the job's category name is matched. A null index (workers read failed / loading)
 * means no filtering.
 */
export function splitByCategory<T extends { workerId: string }>(
  items: T[],
  index: WorkerCategoryIndex | null,
  job: { categoryId: string | null | undefined; categoryName: string | null | undefined },
): { matching: T[]; hidden: T[] } {
  if (!index) return { matching: items, hidden: [] }
  const matches = (item: T) => {
    const info = index.get(item.workerId)
    return job.categoryId !== undefined ? workerMatchesCategory(info, job.categoryId) : workerMatchesCategoryName(info, job.categoryName)
  }
  return { matching: items.filter(matches), hidden: items.filter((item) => !matches(item)) }
}

/**
 * Category id to match for a job: from the job detail when loaded (null = the job has no
 * category, so everyone matches); undefined = not known yet, so the category name is matched.
 */
export function resolveJobCategoryId(
  jobDetail: { category?: string | number | null } | null | undefined,
  categoryName: string | null,
): string | null | undefined {
  if (!jobDetail) return undefined
  const id = jobDetail.category == null ? '' : String(jobDetail.category).trim()
  if (id) return id
  return categoryName ? undefined : null
}
